import { PrismaEnums, isAirAdmin, isCoachRole } from '@airreading/common';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown, Ellipsis } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import { validateRouteRole } from '@/lib/routing';

import { RolesLabelMap, allRoles } from '@/auth/roles';
import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RoleBadgeList } from '@/components/users/role-badge';

import { EmptyArray, getUsers } from '@/lib/api-calls';
import { usePaginatedFilters } from '@/lib/filtering';
import { paginationRequestSchema } from '@/lib/pagination';
import { useNavigateSearchState } from '@/lib/routing';

import { useOrgOptions, useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { useErrorToast } from '@/lib/error-handling';
import { useSorting } from '@/lib/sorting';
import { airColumnHelper } from '@/lib/tables';
import { CreateCoach } from '@/routes/_layout/users/-components/create-coach';
import { CreateCoachesByCsv } from '@/routes/_layout/-components/create-coaches-by-csv';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv';
import { CreateUser } from '@/routes/_layout/users/-components/create-user';
import { ManageAirAdmin } from '@/routes/_layout/users/-components/manage-air-admin';
import { ManageCoach } from '@/routes/_layout/users/-components/manage-coach';
import { ManageNonCoreRoles } from '@/routes/_layout/users/-components/manage-non-core-roles';
import { ManageStudent } from '@/routes/_layout/users/-components/manage-student';
import { PromoteCoach } from '@/routes/_layout/users/-components/promote-coach';
import {
  GetUsersSortableColumn,
  UserRoleStatus,
  UserWithRolesAndProfiles,
  getUsersSortOrderSchema,
  getUsersSortableColumnSchema,
} from '@airreading/common/data';
import { ImportCoachesFromV1 } from './-components/import-coaches-from-v1';

const usersSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  roles: z.array(z.nativeEnum(PrismaEnums.Role)).optional(),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  sortBy: getUsersSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/users/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: UsersIndex,
  validateSearch: usersSearchSchema,
});

const col = airColumnHelper<UserWithRolesAndProfiles>(getUsersSortableColumnSchema);
const columns = [
  col.accessor((row) => row.id, {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link to={'/users/$userId'} params={{ userId: getValue() }}>
        {row.original.fullNameOrEmail}
      </Link>
    ),
  }),

  col.accessor('email', {
    id: 'email',
    header: 'Email',
  }),

  col.accessor('timeZone', {
    id: 'timeZone',
    header: 'Time zone',
  }),

  col.accessor('userRoles', {
    id: 'roles',
    header: 'Roles',
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <RoleBadgeList userRoles={getValue()} />
        </div>
      );
    },
  }),

  col.display({
    id: 'actions',
    cell: ({ row: { original: user } }) => (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
            <Ellipsis className="h-4 w-4" />
            <span className="sr-only">Open user action menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {user.userRoles.some(isCoachRole) && (
            <ManageCoach
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  {user.userRoles.some((role) => isCoachRole(role) && role.status === UserRoleStatus.active)
                    ? 'Revoke teacher role'
                    : 'Restore teacher role'}
                </DropdownMenuItem>
              }
              user={user}
            />
          )}

          {!user.userRoles.some(isCoachRole) && (
            <PromoteCoach
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Promote as teacher</DropdownMenuItem>}
              user={user}
            />
          )}
          <ManageAirAdmin
            user={user}
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                {isAirAdmin(user) ? 'Revoke Air Admin' : 'Promote as Air Admin'}
              </DropdownMenuItem>
            }
          />
          {user.studentProfile && (
            <ManageStudent
              user={user}
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Manage student profile</DropdownMenuItem>}
            />
          )}
          {!user.studentProfile && (
            <CreateStudent
              trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Create student profile</DropdownMenuItem>}
              fromUser={user}
            />
          )}
          <ManageNonCoreRoles
            trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Manage other roles</DropdownMenuItem>}
            user={user}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  }),
];

function UsersIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      roles: [[selectedRoles, activeSelectedRoles], setSelectedRoles],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'roles', 'schools', 'orgs']);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetUsersSortableColumn>([sortBy, didChangeSortBy]);

  const allOrganizationOptions = useOrgOptions();
  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: activeSelectedOrgs ?? [],
  });
  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getUsers({
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        roles: activeSelectedRoles,
        schools: activeSelectedSchools,
        orgs: activeSelectedOrgs,
      },
      sortBy,
    }),
  );

  const users = useMemo(() => response?.users || EmptyArray, [response]);
  useErrorToast(error, 'Error loading users. Please try again.');

  const table = useReactTable({
    data: users,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
    },
    rowCount: response?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Roles"
          options={allRoles.map((role) => ({ label: RolesLabelMap[role], value: role }))}
          selectedValues={selectedRoles ?? []}
          onChange={setSelectedRoles}
        />
        <TableFilterDropdown
          title="Districts"
          options={allOrganizationOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="Schools"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userNameQuery,
      setUserNameQuery,
      selectedRoles,
      setSelectedRoles,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      allOrganizationOptions,
      selectedOrgs,
      setSelectedOrgs,
      resetFilters,
    ],
  );

  useEffect(() => {
    if (!selectedSchools || selectedSchools.length === 0) return;

    const validSchoolIds = allSchoolOptions.map((s) => s.value);
    const stillValid = selectedSchools.filter((id) => validSchoolIds.includes(id));

    if (stillValid.length !== selectedSchools.length) {
      setSelectedSchools(stillValid.length > 0 ? stillValid : undefined);
    }
  }, [activeSelectedOrgs, allSchoolOptions, selectedSchools, setSelectedSchools]);

  return (
    <InfoPage
      headerText="Accounts"
      headerActions={
        <>
          {/* See https://ui.shadcn.com/docs/components/dialog#notes and https://github.com/radix-ui/primitives/issues/1836 
        to fix Dialog inside Dropdown issue */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button className="ml-auto hidden h-8 lg:flex">
                Create users
                <ChevronDown className="mr-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[150px]">
              <CreateStudent trigger={<DropdownMenuItem onSelect={preventDefault}>Create student</DropdownMenuItem>} />
              <CreateCoach trigger={<DropdownMenuItem onSelect={preventDefault}>Create teacher</DropdownMenuItem>} />
              <CreateUser trigger={<DropdownMenuItem onSelect={preventDefault}>Create other user</DropdownMenuItem>} />
              <CreateStudentsByCsv
                trigger={<DropdownMenuItem onSelect={preventDefault}>Create students by CSV</DropdownMenuItem>}
                from="users"
              />
              <CreateCoachesByCsv
                trigger={<DropdownMenuItem onSelect={preventDefault}>Create teachers by CSV</DropdownMenuItem>}
                from="users"
              />
              <ImportCoachesFromV1
                trigger={<DropdownMenuItem onSelect={preventDefault}>Import teachers from V1</DropdownMenuItem>}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    >
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No users found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
      />
    </InfoPage>
  );
}

function preventDefault(e: Event): void {
  e.preventDefault();
}
